// Előfizetés — adatbetöltés és a fizetés indítása.
//
// Az előfizetés ÁLLAPOTÁT a kliens csak olvassa. Írni kizárólag a szerver ír:
// a `subscriptionClock` óra és a Stripe-webhook. Ez nem kényelmi kérdés — egy
// kliensoldalon állítható lejárat nem korlát, hanem díszlet.

import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from './firebase';
import { language } from './i18n';
import type { SubscriptionView } from './session';
import { subscriptionFrom } from './session';

export type CompanyBilling = {
  id: string;
  name: string;
  /// Hány felhasználó tartozik a céghez.
  ///
  /// A `users/{uid}.companyIds` tükörből számoljuk — ugyanabból, amit a
  /// biztonsági szabályok is néznek, tehát nem csúszhat el a kettő.
  memberCount: number;
  /// Hány embernek van már AI-s helye.
  aiAssigned: number;
  subscription?: SubscriptionView;
};

/// MINDEN cég előfizetés-adata — a szuperadmin áttekintőjéhez.
///
/// A cégek listázását a biztonsági szabály CSAK szuperadminnak engedi; más
/// felhasználónál a lekérdezés egészében elhasal, nem részlegesen. Ezért a
/// hívónak számítania kell a hibára — nem „üres lista" jön, hanem kivétel.
export async function loadAllCompanyBilling(): Promise<CompanyBilling[]> {
  const [companies, users] = await Promise.all([
    getDocs(collection(db, 'projects')),
    getDocs(collection(db, 'users')),
  ]);

  const tagok = new Map<string, number>();
  const aiHelyek = new Map<string, number>();

  for (const user of users.docs) {
    const data = user.data();
    const cegek = Array.isArray(data['companyIds']) ? (data['companyIds'] as string[]) : [];
    const aiE = data['seatTier'] === 'ai';
    for (const ceg of cegek) {
      tagok.set(ceg, (tagok.get(ceg) ?? 0) + 1);
      if (aiE) aiHelyek.set(ceg, (aiHelyek.get(ceg) ?? 0) + 1);
    }
  }

  return companies.docs
    .map((snapshot) => {
      const data = snapshot.data();
      return {
        id: snapshot.id,
        name: typeof data['name'] === 'string' ? data['name'] : snapshot.id,
        memberCount: tagok.get(snapshot.id) ?? 0,
        aiAssigned: aiHelyek.get(snapshot.id) ?? 0,
        subscription: subscriptionFrom(data['subscription']),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'hu'));
}

/// Egy cég kiosztott helyei — a saját beállítások kártyájához.
export async function loadSeatUsage(companyId: string): Promise<{ basic: number; ai: number }> {
  const users = await getDocs(collection(db, 'users'));
  let basic = 0;
  let ai = 0;
  for (const user of users.docs) {
    const data = user.data();
    const cegek = Array.isArray(data['companyIds']) ? (data['companyIds'] as string[]) : [];
    if (!cegek.includes(companyId)) continue;
    if (data['seatTier'] === 'ai') ai += 1;
    else basic += 1;
  }
  return { basic, ai };
}

/// A cég előfizetésének friss beolvasása (a fizetésből visszatérve).
export async function reloadSubscription(
  companyId: string,
): Promise<SubscriptionView | undefined> {
  const snapshot = await getDoc(doc(db, 'projects', companyId));
  return subscriptionFrom(snapshot.data()?.['subscription']);
}

/// A Stripe fizetési oldalának megnyitása.
///
/// A hívás SZERVEROLDALON ellenőrzi, hogy a hívó tényleg cégadmin — a kliens
/// állítása erről nem bizonyíték. A visszakapott URL a Stripe saját
/// felülete: kártyaadat SOHA nem halad át ezen az appon.
export async function startCheckout(input: {
  companyId: string;
  basicSeats: number;
  aiSeats: number;
}): Promise<string> {
  // A PÉNZNEM A FELÜLET NYELVÉBŐL jön. A Stripe-árak HUF-ot és EUR-t is
  // tartalmaznak; ha a munkamenet nem ad meg pénznemet, a Stripe az ár
  // alapértelmezettjére (HUF) esik vissza. A magyar oldal forintot hirdet, az
  // angol eurót — pénznem nélkül az angol látogató eurós árat látna a lapon és
  // forintos fizetést a Stripe-nál.
  //
  // A szerver újraellenőrzi: a kliens állítása itt sem bizonyíték.
  const call = httpsCallable<typeof input & { currency: string }, { url?: string }>(
    functions,
    'createCheckoutSession',
  );
  const result = await call({ ...input, currency: language() === 'en' ? 'eur' : 'huf' });
  const url = result.data?.url;
  if (typeof url !== 'string' || url.length === 0) {
    throw new Error('A Stripe nem adott fizetési hivatkozást.');
  }
  return url;
}
